import { gql } from '~gql-types/gql'

/** @public */
export const USER_BOARD_FRAGMENT = gql(`
  fragment UserBoardFields on Board {
    disabledEmojis
    features
    useCase
    useCaseCategory
    createdAt
    previouslyHadFreemiumLicense
    owner {
      avatar
      firstname
      id
      lastname
      legacyEid
      username
      followsUser
      profilePhotoUrl
    }
    settings {
      id
      readOnly
      gradingEnabled
      groupsEnabled
      groupVisibility
      communityMentionsEnabled
      disabledInsightSubcategories
      permissionLevel
      discoverable
    }
    network {
      id
      name
      title
      settings {
        id
        dateFormat
        paymentType
        studentPaymentPrice
        studentPaymentLicenseDuration
      }
      features
      defaultBoard {
        id
      }
    }
    id
    title
    description
    timezone
    pinnedPost {
      id
    }
    badges {
      id
      name
      symbol
      color
      pointValue
    }
    gradingPeriods {
      id
      startDate
      endDate
    }
    onboardingStatus {
      dismissed
      stepsAcknowledged
    }
    appliedLicense {
      id
      expiresAt
      activeLicense {
        id
        type
      }
    }
    paymentNetwork {
      id
      title
    }
    boardPaymentSetting {
      id
      paymentType
    }
    expiredUserPayment {
      id
    }
  }
`)

export const FETCH_BOARD_LAYOUT = gql(`
  query FetchBoardUser($boardId: ID!, $networkName: String!) {
    loggedInUser {
      id
      firstname
      lastname
      username
      primaryEmail {
        id
        text
      }
      networkUser(networkName: $networkName) {
        userId
        networkId
        role
      }
      boardUser(id: $boardId) {
        __typename
        ... on BoardMember {
          userId
          boardId
          role
          shouldHitPaywall
          onboardingStatus
          feedLastAcknowledgedAt
          roleV2 {
            id
            definition {
              id
              name
              permissions
            }
          }
          board {
            ...UserBoardFields
          }
        }
        ... on BoardViewer {
          boardId
          userId
          board {
            ...UserBoardFields
          }
        }
      }
    }
  }
`)

export const FETCH_USER = gql(`
  query FetchLoggedInUser {
    loggedInUser {
      id
      firstname
      lastname
      username
      avatar
      legacyEid
      profilePhotoUrl
      coverPhotoUrl
    }
  }
`)

export const FETCH_NETWORK_USER = gql(`
  query FetchNetworkUser($networkName: String!) {
    loggedInUser {
      id
      firstname
      lastname
      username
      networkUser(networkName: $networkName) {
        userId
        networkId
        role
      }
    }
  }
`)

export const FETCH_USER_LAYOUT_ACCESS = gql(`
  query FetchUserLayoutAccess($boardId: ID!, $networkName: String!) {
    loggedInUser {
      id
      firstname
      lastname
      username
      networkUser(networkName: $networkName) {
        userId
        networkId
        role
      }
      boardUser(id: $boardId) {
        __typename
        ... on BoardMember {
          userId
          boardId
          role
        }
        ... on BoardViewer {
          userId
          boardId
        }
      }
    }
  }
`)

/** @public */
export const USER_BOARD_DRAWER_FRAGMENT = gql(`
  fragment UserBoardDrawerFields on Board {
    id
    title
    description
    timezone
    disabledEmojis
    useCaseCategory
    features
    serviceUrl
    previouslyHadFreemiumLicense
    roleDefinitions {
      id
      name
      permissions
    }
    owner {
      avatar
      firstname
      id
      lastname
      legacyEid
      username
      followsUser
      profilePhotoUrl
    }
    appliedLicense {
      id
      expiresAt
      activeLicense {
        id
        type
      }
    }
    settings {
      id
      readOnly
      gradingEnabled
      gradePassbackFrequency
      groupVisibility
      permissionLevel
      communityMentionsEnabled
      discoverable
      disabledInsightSubcategories
    }
    network {
      id
      name
      title
      features
      settings {
        id
        dateFormat
        paymentType
        studentPaymentPrice
        studentPaymentLicenseDuration
      }
      defaultBoard {
        id
      }
    }
    paymentNetwork {
      id
      name
      title
    }
    ltiIds
    joinRequestCount
    lti3Integrations {
      id
      lineitemServiceUrl
      namesAndRolesUrl
      lineitems {
        id
        url
      }
    }
    onboardingStatus {
      dismissed
      stepsAcknowledged
    }
    expiredUserPayment {
      id
    }
    boardPaymentSetting {
      id
      paymentType
    }
  }
`)

export const FETCH_BOARD_DRAWER = gql(`
  query FetchBoardDrawer($boardId: ID!, $networkName: String!) {
    loggedInUser {
      id
      firstname
      lastname
      username
      primaryEmail {
        id
        text
      }
      networkUser(networkName: $networkName) {
        userId
        networkId
        role
      }
      boardUser(id: $boardId) {
        __typename
        ... on BoardMember {
          userId
          boardId
          role
          shouldHitPaywall
          onboardingStatus
          feedLastAcknowledgedAt
          appliedLicenseRestrictions
          roleV2 {
            id
            definition {
              id
              name
              permissions
            }
          }
          onboardingV2Flow {
            completedAt
            id
            steps {
              completedAt
              id
              name
              order
              status
            }
          }
          board {
            ...UserBoardDrawerFields
          }
        }
        ... on BoardViewer {
          boardId
          userId
          appliedLicenseRestrictions
          board {
            ...UserBoardDrawerFields
          }
        }
      }
    }
  }
`)
